/**
 * Alarme de fin de repos (Web Audio, aucun fichier son à charger) et vibration
 * là où le téléphone le permet (Android ; Safari iOS n'expose pas la vibration).
 *
 * Elle doit s'entendre dans une salle de sport, téléphone posé sur un banc :
 * trois salves de deux bips (aigu puis plus aigu), en onde triangle — plus
 * « perçante » qu'une sinusoïde au même volume — à pleine voix, derrière un
 * limiteur qui évite la saturation.
 *
 * Les navigateurs mobiles ne laissent jouer un son qu'après un geste de
 * l'utilisateur : primeRestAlarm() doit être appelé dans un tap — la
 * validation d'une série, qui lance justement le repos — pour déverrouiller
 * le contexte audio. Sans ce déverrouillage, l'alarme se contente de vibrer.
 */
let audioContext = null;

// `note(key, start)` reçoit la durée de chaque appel audio (lib/validationTiming).
export const primeRestAlarm = (note) => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    let start = performance.now();
    // iOS : une session « transient » passe par-dessus la musique de l'élève
    // (Spotify, Apple Music) en la baissant le temps du bip, au lieu de la
    // couper — et sans être noyée dedans.
    if (navigator.audioSession && navigator.audioSession.type !== 'transient') {
      try { navigator.audioSession.type = 'transient'; } catch { /* non modifiable */ }
      note?.('audio_session_ms', start);
    }
    start = performance.now();
    if (!audioContext) {
      audioContext = new AudioCtx();
      note?.('audio_create_ms', start);
    }
    if (audioContext.state === 'suspended') {
      start = performance.now();
      audioContext.resume().then(() => note?.('audio_resume_ms', start), () => {});
      note?.('audio_resume_call_ms', start);
    }
  } catch {
    // Pas d'audio disponible : l'alarme vibrera seulement.
  }
};

// Trois salves, deux notes chacune (si5 puis mi6) : reconnaissable, et assez
// long (~1,4 s) pour être entendu même si l'élève parle ou range un disque.
const BURSTS = [0, 0.5, 1.0];
const NOTES = [
  { offset: 0, frequency: 988 },
  { offset: 0.16, frequency: 1319 },
];
const NOTE_LENGTH = 0.14;
const PEAK_GAIN = 0.9;
const VIBRATION = [400, 150, 400, 150, 400, 150, 700];

export const ringRestAlarm = () => {
  try {
    navigator.vibrate?.(VIBRATION);
  } catch {
    // Vibration refusée : on garde le son.
  }
  try {
    if (!audioContext || audioContext.state !== 'running') return;
    const start = audioContext.currentTime + 0.02;
    // Limiteur : pleine voix sans craquement quand deux notes se chevauchent.
    const limiter = audioContext.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 0;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.1;
    limiter.connect(audioContext.destination);

    BURSTS.forEach((burst) => {
      NOTES.forEach(({ offset, frequency }) => {
        const at = start + burst + offset;
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        oscillator.type = 'triangle';
        oscillator.frequency.value = frequency;
        // Attaque et relâche courtes : un bip net, sans claquement.
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, at + 0.01);
        gain.gain.setValueAtTime(PEAK_GAIN, at + NOTE_LENGTH - 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + NOTE_LENGTH);
        oscillator.connect(gain).connect(limiter);
        oscillator.start(at);
        oscillator.stop(at + NOTE_LENGTH + 0.02);
      });
    });
  } catch {
    // Le son est un bonus : jamais d'erreur pour lui.
  }
};

/**
 * Bip court du chrono EMOM : `'tick'` pour 3-2-1 avant un départ, `'go'` au
 * départ de chaque minute (plus aigu, plus long, avec une vibration). Bien
 * plus discret que l'alarme de repos : il sonne toutes les minutes.
 */
const TICKS = {
  tick: { frequency: 880, length: 0.08, gain: 0.5, vibrate: 0 },
  go: { frequency: 1319, length: 0.28, gain: 0.9, vibrate: 250 },
};

export const ringEmomTick = (kind = 'go') => {
  const tick = TICKS[kind] || TICKS.go;
  if (tick.vibrate) {
    try { navigator.vibrate?.(tick.vibrate); } catch { /* vibration refusée */ }
  }
  try {
    if (!audioContext || audioContext.state !== 'running') return;
    const at = audioContext.currentTime + 0.02;
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.type = 'triangle';
    oscillator.frequency.value = tick.frequency;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(tick.gain, at + 0.01);
    gain.gain.setValueAtTime(tick.gain, at + tick.length - 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + tick.length);
    oscillator.connect(gain).connect(audioContext.destination);
    oscillator.start(at);
    oscillator.stop(at + tick.length + 0.02);
  } catch {
    // Le son est un bonus : jamais d'erreur pour lui.
  }
};

/** Clé d'un repos donné : une séance, un départ. */
export const restAlarmKey = (sessionId, rest) => `${sessionId}:${rest?.startedAt ?? ''}`;

// Repos déjà sonnés : l'écran de séance (useSessionRest) et la capsule
// flottante surveillent tous deux la fin du repos ; un seul des deux sonne.
const rung = new Set();

export const ringRestAlarmOnce = (key) => {
  if (rung.has(key)) return false;
  rung.add(key);
  ringRestAlarm();
  return true;
};
